import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ForumIcon from '@mui/icons-material/Forum';
import {
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useContactpersonen } from '../../api/klanten.ts';
import type { Contactpersoon } from '../../api/typen.ts';
import { ContactmomentDialoog } from '../../componenten/ContactmomentDialoog.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Lijstpaginering } from '../../componenten/Paginering.tsx';
import { BedrijfVerwijzing } from '../../componenten/Verwijzingen.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { ofStreepje, volledigeNaam } from '../../helpers/formatteer.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { ContactpersoonFormulier } from '../bedrijven/ContactpersoonFormulier.tsx';

/**
 * Het overzicht van alle contactpersonen, over bedrijven heen. Een contactpersoon toevoegen gaat vanaf zijn bedrijf.
 * @returns De pagina.
 */
export function ContactpersonenPagina() {
  const { zoek, offset, limiet, zet, zetOffset } = useZoekparameters();
  const contactpersonen = useContactpersonen(zoek, { limiet, offset });
  const [gesprek, setGesprek] = useState<Contactpersoon | null>(null);
  const [bewerken, setBewerken] = useState<Contactpersoon | null>(null);

  return (
    <Pagina titel="Contactpersonen" ondertitel="Een contactpersoon voeg je toe op de pagina van zijn bedrijf.">
      <Paper sx={{ p: 2, mb: 2 }}>
        <Zoekveld waarde={zoek} onChange={(waarde) => zet({ zoek: waarde })} placeholder="Zoek op naam of e-mail" />
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Laadstatus query={contactpersonen} leeg="Geen contactpersonen gevonden.">
          {(lijst) => (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Naam</TableCell>
                    <TableCell>Functie</TableCell>
                    <TableCell>Bedrijf</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Telefoon</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lijst.map((contactpersoon) => (
                    <TableRow key={contactpersoon.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{volledigeNaam(contactpersoon)}</TableCell>
                      <TableCell>{ofStreepje(contactpersoon.functie)}</TableCell>
                      <TableCell>
                        <BedrijfVerwijzing id={contactpersoon.bedrijfId} />
                      </TableCell>
                      <TableCell>
                        {contactpersoon.email ? (
                          <Link href={`mailto:${contactpersoon.email}`}>{contactpersoon.email}</Link>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell>
                        {contactpersoon.telefoon ? (
                          <Link href={`tel:${contactpersoon.telefoon}`}>{contactpersoon.telefoon}</Link>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Gesprek vastleggen">
                          <IconButton
                            size="small"
                            aria-label="Gesprek vastleggen"
                            onClick={() => setGesprek(contactpersoon)}
                          >
                            <ForumIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" aria-label="Bewerken" onClick={() => setBewerken(contactpersoon)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Laadstatus>
        <Lijstpaginering
          offset={offset}
          limiet={limiet}
          aantal={contactpersonen.data?.length ?? 0}
          onOffset={zetOffset}
        />
      </Paper>
      <ContactmomentDialoog
        open={gesprek !== null}
        onSluit={() => setGesprek(null)}
        contactpersoon={gesprek ?? undefined}
      />
      {bewerken && (
        <ContactpersoonFormulier
          open
          onSluit={() => setBewerken(null)}
          bedrijfId={bewerken.bedrijfId}
          bestaand={bewerken}
        />
      )}
    </Pagina>
  );
}
